import { createContext, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";

import ConfigService from "@/services/configService";
import NotificationsService from "@/services/notificationsService";
import UsersService from "@/services/users";
import { getSocket, disconnectSocket } from "@/services/socketService";
import { toast } from "react-toastify";
import ChatService from "@/services/ChatService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  OVERVIEW_QUERY_KEYS,
  QUERY_REFRESH_INTERVALS,
} from "@/constants/queryRefresh";
import { useChatCallManager } from "@/hooks/useChatCallManager";
import { CallConfirmationModal } from "@/components/ChatCall/CallConfirmationModal";
import { IncomingCallOverlay } from "@/components/ChatCall/IncomingCallOverlay";
import { OngoingCallOverlay } from "@/components/ChatCall/OngoingCallOverlay";
import type {
  ChatCallConfirmationResult,
  ChatCallParticipant,
} from "@/types/chatCall";

// Gerenciador de áudio para notificações (singleton)
let notificationSound: HTMLAudioElement | null = null;
let audioUnlocked = false;

function unlockAudio() {
  if (audioUnlocked) return;

  // Criar o elemento de áudio uma vez
  if (!notificationSound) {
    notificationSound = new Audio("/notification-chat.mp3");
    notificationSound.volume = 0.5;
  }

  // Tocar um som silencioso para "desbloquear" o áudio
  notificationSound.volume = 0;
  notificationSound
    .play()
    .then(() => {
      notificationSound!.pause();
      notificationSound!.currentTime = 0;
      notificationSound!.volume = 0.5;
      audioUnlocked = true;
      // Remover os listeners após desbloquear
      document.removeEventListener("click", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
      document.removeEventListener("touchstart", unlockAudio);
    })
    .catch(() => {
      // Ainda não foi possível desbloquear, tentar novamente na próxima interação
    });
}

// Adicionar listeners para desbloquear áudio na primeira interação
if (typeof document !== "undefined") {
  document.addEventListener("click", unlockAudio, { once: false });
  document.addEventListener("keydown", unlockAudio, { once: false });
  document.addEventListener("touchstart", unlockAudio, { once: false });
}

export function playNotificationSound() {
  if (!notificationSound) {
    notificationSound = new Audio("/notification-chat.mp3");
    notificationSound.volume = 0.5;
  }

  // Resetar e tocar
  notificationSound.currentTime = 0;
  notificationSound.play().catch(() => {
    console.log(
      "Áudio bloqueado pelo navegador. Interaja com a página para habilitar sons.",
    );
  });
}

type ChatListMessage = {
  id?: string | number;
  sender_id?: string | number;
  senderId?: string | number;
  usuario_send?: string | number;
};

type ChatListRoom = {
  id?: string | number;
  unread_count?: number;
  unreadCount?: number;
  lastMessage?: ChatListMessage;
  last_message?: ChatListMessage;
  [key: string]: unknown;
};

const getChatUnreadCount = (chat: ChatListRoom) =>
  Number(chat?.unread_count ?? chat?.unreadCount ?? 0);

const getChatLastMessage = (chat: ChatListRoom) =>
  chat?.lastMessage ?? chat?.last_message;

const getMessageSenderId = (message?: ChatListMessage) =>
  message?.sender_id ?? message?.senderId ?? message?.usuario_send;

const normalizeChatRoom = (chat: ChatListRoom) => ({
  ...chat,
  unread_count: getChatUnreadCount(chat),
  lastMessage: getChatLastMessage(chat),
});

const chatListFingerprint = (chats: ChatListRoom[]) =>
  chats
    .map((chat) => {
      const lastMessage = getChatLastMessage(chat);
      return `${chat.id}:${getChatUnreadCount(chat)}:${lastMessage?.id || ""}`;
    })
    .join(",");

interface LayoutProps {
  fullLoading: boolean;
  setFullLoading: (value: boolean) => void;
  getConfig: any;
  errorGlobal: boolean;
  setErrorGlobal: (value: boolean) => void;
  userLogged: any;
  setUserLogged: (value: boolean) => void;
  getAllUser: any;
  setGetAllUser: (value: boolean) => void;
  notifications: any;
  setNotifications: (value: any) => void;
  chats: any;
  setChats: (value: any) => void;
  excelDashboard: any;
  setExcelDashboard: (value: any) => void;
  refreshNotifications: any;
  setRefreshNotifications: (value: any) => void;
  menuOpen: any;
  setMenuOpen: (value: any) => void;
  getMe: (onlyGetMe?: boolean) => void;
  getChats: () => void;
  openChats: any[];
  setOpenChats: (value: any[] | ((prev: any[]) => any[])) => void;
  dataNotificacoes: any;
  setSearchChatsRecentes: (value: string) => void;
  searchChatsRecentes: any;
  openIAChat: boolean;
  setOpenIAChat: (value: boolean) => void;
  empresaId: string | null;
  setEmpresaId: (value: string | null) => void;
  activeChatCall: any;
  isChatCallBusy: boolean;
  startChatVoiceCall: (room: any) => Promise<void>;
  closeChatCallForRoom: (roomId: string | number, reason?: string) => Promise<void>;
  isChatCallConfirmationOpen: boolean;
  chatCallConfirmationParticipant: ChatCallParticipant | null;
  chatCallConfirmationResult: ChatCallConfirmationResult | null;
  confirmChatVoiceCall: () => Promise<void>;
  cancelChatVoiceCallConfirmation: () => void;
  retryChatVoiceCallConfirmation: () => void;
  isIncomingChatCallOpen: boolean;
  isOngoingChatCallOpen: boolean;
  chatCallRingtoneBlocked: boolean;
  chatCallDurationSeconds: number;
  isChatCallMuted: boolean;
  answerIncomingChatCall: () => Promise<void>;
  rejectIncomingChatCall: () => Promise<void>;
  cancelOutgoingChatCall: () => Promise<void>;
  endActiveChatCall: () => Promise<void>;
  toggleActiveChatCallMicrophone: () => Promise<void>;
  chatCallConnectionState: "idle" | "connecting" | "connected" | "reconnecting";
  logout: (refreshPage?: boolean) => void;
}

const layoutContext = createContext({} as LayoutProps);

export const useLayout = (): LayoutProps => {
  return useContext(layoutContext);
};

function useProvideLayout(): LayoutProps {
  const [fullLoading, setFullLoading] = useState(true);
  const [errorGlobal, setErrorGlobal] = useState(false);
  const [userLogged, setUserLogged] = useState<any>({});
  const [getConfig, setGetConfig] = useState<any>({});
  const [getAllUser, setGetAllUser] = useState<any>({});
  const [initMe, setInitMe] = useState(false);
  const [notifications, setNotifications] = useState<any>([]);
  const [refreshNotifications, setRefreshNotifications] = useState<any>(false);
  const [excelDashboard, setExcelDashboard] = useState<any>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [chats, setChats] = useState<any[]>([]);
  const [openChats, setOpenChats] = useState<any[]>([]);
  const [searchChatsRecentes, setSearchChatsRecentes] = useState<any>("");
  const [openIAChat, setOpenIAChat] = useState(false);
  const { token } = JSON.parse(localStorage.getItem("token") || "{}");
  const [empresaId, setEmpresaId$] = useState<string | null>(
    window.localStorage.getItem("empresaId"),
  );
  const lastNotificationsSyncRef = useRef(0);
  const lastOverviewInvalidationRef = useRef(0);
  const chatsRef = useRef<ChatListRoom[]>([]);
  const chatListRequestSeqRef = useRef(0);
  const chatUpdateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const queryClient = useQueryClient();
  const chatCallManager = useChatCallManager({
    enabled: Boolean(initMe && userLogged.data?.id),
    userId: userLogged.data?.id,
  });

  const {
    isPending,
    data: dataNotificacoes,
    refetch,
  } = useQuery({
    queryKey: [`dataNotificacoes`, token],
    queryFn: async () => await NotificationsService.get(),
    enabled: !!token,
    retry: 3,
  });

  useEffect(() => {
    if (!userLogged.data?.id) {
      getMe();
    }
  }, [userLogged]);

  useEffect(() => {
    if (!isPending && !!dataNotificacoes?.data?.data) {
      setNotifications(dataNotificacoes?.data?.data || []);
    }
  }, [dataNotificacoes, isPending]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // Configuração dos listeners de socket
  useEffect(() => {
    if (!initMe || !userLogged.data?.id) return;

    const socket = getSocket();
    const userId = userLogged.data.id;

    const notificationHandler = (data: any) => {
      setNotifications((prevNotifications: any) => [
        data,
        ...prevNotifications,
      ]);

      const now = Date.now();
      if (
        now - lastNotificationsSyncRef.current >
        QUERY_REFRESH_INTERVALS.notificationsSync
      ) {
        lastNotificationsSyncRef.current = now;
        refetch();
      }

      queryClient.invalidateQueries({ queryKey: ["comentarios-nota"] });
      queryClient.invalidateQueries({ queryKey: ["comentarios-tarefa"] });

      if (
        now - lastOverviewInvalidationRef.current >
        QUERY_REFRESH_INTERVALS.overviewInvalidation
      ) {
        lastOverviewInvalidationRef.current = now;
        OVERVIEW_QUERY_KEYS.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: [key] });
        });
      }
    };

    const setupListeners = () => {
      socket.off(`notification:${userId}`, notificationHandler);
      socket.on(`notification:${userId}`, notificationHandler);
    };

    // Se o socket já está conectado, configurar imediatamente
    if (socket.connected) {
      setupListeners();
      return () => {
        socket.off(`notification:${userId}`, notificationHandler);
      };
    }

    const onConnect = () => {
      setupListeners();
    };

    socket.on("connect", onConnect);

    // Tentar conectar se não estiver conectado
    if (!socket.connected) {
      socket.connect();
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off(`notification:${userId}`, notificationHandler);
    };
  }, [initMe, userLogged.data?.id]);

  // Listener global para atualização de chats (funciona em qualquer lugar do sistema)
  useEffect(() => {
    if (!initMe || !userLogged.data?.id) return;

    const socket = getSocket();
    const userId = userLogged.data.id;
    const eventName = `notification:chat:atualizarlistagemdechats:${userId}`;

    const chatUpdateHandler = () => {
      if (chatUpdateDebounceRef.current) {
        clearTimeout(chatUpdateDebounceRef.current);
      }

      chatUpdateDebounceRef.current = setTimeout(() => {
        void getChats(true, userId);
      }, 150);
    };

    const setupChatListener = () => {
      socket.off(eventName, chatUpdateHandler);
      socket.on(eventName, chatUpdateHandler);
    };

    // Se o socket já está conectado, configurar imediatamente
    if (socket.connected) {
      setupChatListener();
    } else {
      const onConnect = () => {
        setupChatListener();
      };
      socket.on("connect", onConnect);

      // Tentar conectar
      socket.connect();

      return () => {
        socket.off("connect", onConnect);
        socket.off(eventName, chatUpdateHandler);
        if (chatUpdateDebounceRef.current) {
          clearTimeout(chatUpdateDebounceRef.current);
          chatUpdateDebounceRef.current = null;
        }
      };
    }

    return () => {
      socket.off(eventName, chatUpdateHandler);
      if (chatUpdateDebounceRef.current) {
        clearTimeout(chatUpdateDebounceRef.current);
        chatUpdateDebounceRef.current = null;
      }
    };
  }, [initMe, userLogged.data?.id, searchChatsRecentes]);

  async function getChats(shouldNotify = false, notifyUserId = userLogged.data?.id) {
    const requestSeq = ++chatListRequestSeqRef.current;

    try {
      const res =
        await ChatService.listarConversasRecentes(searchChatsRecentes);

      if (requestSeq !== chatListRequestSeqRef.current) return;

      if (res?.data?.success) {
        const newChats = (res?.data?.data || []).map(normalizeChatRoom);
        if (shouldNotify) {
          const hasNewMessages = newChats.some((chat: ChatListRoom) => {
            const prevChat = chatsRef.current.find(
              (item) => item.id === chat.id,
            );
            const lastMessage = getChatLastMessage(chat);
            return (
              getChatUnreadCount(chat) > 0 &&
              getMessageSenderId(lastMessage) !== notifyUserId &&
              (!prevChat ||
                getChatUnreadCount(chat) > getChatUnreadCount(prevChat))
            );
          });

          if (hasNewMessages) {
            playNotificationSound();
          }
        }

        // Só atualiza se os dados realmente mudaram para evitar re-renders cascata
        setChats((prev: any[]) => {
          if (chatListFingerprint(prev) === chatListFingerprint(newChats)) {
            return prev;
          }

          chatsRef.current = newChats;
          return newChats;
        });
      } else {
        console.error("Erro ao listar conversas recentes:", res.message);
      }
    } catch (error) {
      console.error("Erro ao listar conversas recentes:", error);
    }
  }

  async function getMe(onlyGetMe = false) {
    try {
      const { token } = JSON.parse(localStorage.getItem("token") || "{}");
      if (token) {
        const responseMe = await UsersService.me();
        if (responseMe.status === 200) {
          // Só atualiza o estado se os dados realmente mudaram,
          // evitando re-renders desnecessários (flash ao voltar para a aba)
          setUserLogged((prev: any) => {
            if (
              JSON.stringify(prev?.data) ===
              JSON.stringify(responseMe.data?.data)
            ) {
              return prev;
            }
            const userData = responseMe.data?.data;
            if (userData?.id) {
              Sentry.setUser({
                id: String(userData.id),
                email: userData.email,
                username: userData.empresa_id
                  ? String(userData.empresa_id)
                  : undefined,
              });
            }
            return responseMe.data;
          });
          // getNotifications();
          if (!onlyGetMe) {
            // se for somente para pegar o me, não pega os chats
            refetch();
            await getChats();
          }
          setInitMe(true);
        } else {
          toast.error("Não foi possível buscar informações do usuário");
          setErrorGlobal(true);
        }
      }
    } catch (error) {
      toast.error("Não foi possível buscar informações do usuário");
      setErrorGlobal(true);
    } finally {
      setFullLoading(false);
    }
  }

  useEffect(() => {
    if (userLogged.data?.id && !getConfig.ufs) {
      getConfigs();
    }
  }, [userLogged, getConfig]);

  async function getConfigs() {
    try {
      const { token } = JSON.parse(localStorage.getItem("token") || "{}");
      if (token) {
        const responseme = await ConfigService.configs();
        if (responseme.status === 200) {
          setGetConfig(responseme.data);
        } else {
          toast.error("Não foi possível buscar configurações do APP");
        }
      }
    } catch (error) {
      toast.error("Não foi possível buscar configurações do APP");
    }
  }

  function setEmpresaId(value: string | null) {
    if (value) {
      window.localStorage.setItem("empresaId", value);
    } else {
      window.localStorage.removeItem("empresaId");
    }
    setEmpresaId$(value);
  }

  function logout() {
    // Desconecta o socket antes de fazer logout
    disconnectSocket();
    Sentry.setUser(null);
    window.localStorage.clear();
    queryClient.clear();
    setUserLogged({});
    setFullLoading(false);
    /* if (refreshPage) {
      window.location.href = "/login";
    } */
    toast.success("Deslogado com Sucesso!");
  }

  return {
    fullLoading,
    setFullLoading,
    getConfig,
    errorGlobal,
    setErrorGlobal,
    userLogged,
    setUserLogged,
    getAllUser,
    setGetAllUser,
    notifications,
    setNotifications,
    excelDashboard,
    setExcelDashboard,
    refreshNotifications,
    setRefreshNotifications,
    menuOpen,
    setMenuOpen,
    getMe,
    chats,
    setChats,
    getChats,
    openChats,
    setOpenChats,
    dataNotificacoes,
    searchChatsRecentes,
    setSearchChatsRecentes,
    openIAChat,
    setOpenIAChat,
    empresaId,
    setEmpresaId,
    activeChatCall: chatCallManager.activeCall,
    isChatCallBusy: chatCallManager.isCallActionLoading,
    startChatVoiceCall: chatCallManager.requestVoiceCall,
    closeChatCallForRoom: chatCallManager.closeCallForRoom,
    isChatCallConfirmationOpen: chatCallManager.isCallConfirmationOpen,
    chatCallConfirmationParticipant: chatCallManager.callConfirmationParticipant,
    chatCallConfirmationResult: chatCallManager.callConfirmationResult,
    confirmChatVoiceCall: chatCallManager.confirmVoiceCall,
    cancelChatVoiceCallConfirmation: chatCallManager.cancelVoiceCallConfirmation,
    retryChatVoiceCallConfirmation: chatCallManager.retryVoiceCallConfirmation,
    isIncomingChatCallOpen: chatCallManager.isIncomingCallOpen,
    isOngoingChatCallOpen: chatCallManager.isOngoingCallOpen,
    chatCallRingtoneBlocked: chatCallManager.ringtoneBlocked,
    chatCallDurationSeconds: chatCallManager.callDurationSeconds,
    isChatCallMuted: chatCallManager.isCallMuted,
    answerIncomingChatCall: chatCallManager.answerIncomingCall,
    rejectIncomingChatCall: chatCallManager.rejectIncomingCall,
    cancelOutgoingChatCall: chatCallManager.cancelOutgoingCall,
    endActiveChatCall: chatCallManager.endCurrentCall,
    toggleActiveChatCallMicrophone: chatCallManager.toggleCallMicrophone,
    chatCallConnectionState: chatCallManager.callConnectionState,
    logout,
  };
}

const ProvideLayout: any = ({ children }: any) => {
  const layout: LayoutProps = useProvideLayout();
  return (
    <layoutContext.Provider value={layout}>
      {children}

      <CallConfirmationModal
        open={layout.isChatCallConfirmationOpen}
        participant={layout.chatCallConfirmationParticipant}
        result={layout.chatCallConfirmationResult}
        isSubmitting={layout.isChatCallBusy}
        onCancel={layout.cancelChatVoiceCallConfirmation}
        onConfirm={layout.confirmChatVoiceCall}
        onRetry={layout.retryChatVoiceCallConfirmation}
      />

      <IncomingCallOverlay
        open={layout.isIncomingChatCallOpen}
        call={layout.activeChatCall}
        isBusy={layout.isChatCallBusy}
        ringtoneBlocked={layout.chatCallRingtoneBlocked}
        onAnswer={layout.answerIncomingChatCall}
        onReject={layout.rejectIncomingChatCall}
      />

      <OngoingCallOverlay
        open={layout.isOngoingChatCallOpen}
        call={layout.activeChatCall}
        isBusy={layout.isChatCallBusy}
        isMuted={layout.isChatCallMuted}
        durationSeconds={layout.chatCallDurationSeconds}
        connectionState={layout.chatCallConnectionState}
        onCancel={layout.cancelOutgoingChatCall}
        onToggleMute={layout.toggleActiveChatCallMicrophone}
        onEnd={layout.endActiveChatCall}
      />
    </layoutContext.Provider>
  );
};

export default ProvideLayout;
